<template>
    <v-select
        v-model="value"
        :density="selectDensity"
        :items="items"
        :variant="selectVariant"
        :hint="hint ? $t(hint) : ''"
        :item-title="itemTitle"
        :item-value="itemValue"
        :label="$t(label)"
        :prepend-icon="prependIcon">
        <template #item="{props, item}">
            <v-row class="ml-1 mr-1 mb-1 mt-0">
                <v-list-item :width="itemWidth(item.value, value)" v-bind="props"/>
                <slot v-if="showActionBtn(item.value, value)" name="actionBtn">
                    <k-tool-tip v-if="toolTipText"
                                class="ml-2 my-1"
                                location="right"
                                :icon="actionBtnIcon"
                                :is-button="true"
                                :text="toolTipText"
                                :is-loading="isLoading"
                                :variant="actionBtnVariant"
                                :color="actionBtnColor"
                                :size="actionBtnSize"
                                @click="$emit('click', item)">
                    </k-tool-tip>
                    <v-btn v-else
                           :icon="actionBtnIcon"
                           :variant="actionBtnVariant"
                           :color="actionBtnColor"
                           :size="actionBtnSize"
                           @click="$emit('click', item)"
                    />
                </slot>
            </v-row>
        </template>
    </v-select>
</template>

<script lang="ts">
import type {PropType} from "vue"
import {defineComponent, ref} from "vue"
import type {TColor} from "@/components/generics/types/TColor";
import type {TBtnVariant} from "@/components/generics/types/TBtnVariant";
import type {TSize} from "@/components/generics/types/TSize";
import type {TSelectDensity} from "@/components/generics/types/TSelectDensity";
import type {TSelectVariant} from "@/components/generics/types/TSelectVariant";
import {useVModel} from "@vueuse/core";
import KToolTip from "@/components/dds/KToolTip/KToolTip.vue";
import type {TBaseStore} from "@/stores/BaseStore/types/TBaseStore";

type ModelType = string|number

export default defineComponent({
    components: {KToolTip},
    emits: [
        "update:modelValue",
        "click"
    ],
    props: {
        modelValue: {
            type: [String, Number]
        },
        name: {
            type: String
        },
        useActionBtn: {
            type: Boolean,
            default: false
        },
        showActionBtnOnSelected: {
            type: Boolean,
            default: false
        },
        selectDensity: {
            type: String as PropType<TSelectDensity>,
            default: "default"
        },
        selectVariant: {
            type: String as PropType<TSelectVariant>,
            default: "filled"
        },
        actionBtnColor: {
            type: String as PropType<TColor>
        },
        actionBtnVariant: {
            type: String as PropType<TBtnVariant>,
            default: "elevated"
        },
        actionBtnSize: {
            type: String as PropType<TSize>,
            default: "default"
        },
        actionBtnIcon: {
            type: String
        },
        items: {
            type: Object as PropType<object[]>
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        toolTipText: {
            type: String
        },
        store: {
            type: Object as PropType<TBaseStore<string>>,
        },
        itemTitle: {
            type: String,
            required: true
        },
        itemValue: {
            type: String,
            required: true
        },
        label: {
            type: String,
            required: true
        },
        hint: {
            type: String
        },
        prependIcon: {
            type: String
        }
    },

    setup(props, {emit}) {
        const value = useVModel(props, 'modelValue', emit);
        const items = ref();

        if (props.items != undefined) {
            items.value = props.items;
        } else if (props.store != undefined){
            items.value = props.store.items
        } else {
            items.value = []
        }

        const showActionBtn = (item: any, value: any): boolean => {
            if (props.useActionBtn) {
                if (props.showActionBtnOnSelected) {
                    return true
                }
                return item != value
            }
            return false
        }

        const itemWidth = (item: any, value: any): string => {
            return showActionBtn(item, value) ? '80%' : '100%'
        }

        return {
            //composable
            //ref
            value,
            items,
            //action
            showActionBtn,
            itemWidth
        }
    }
})
</script>